// Map a thrown error to learner-facing copy at the UI boundary, so internal
// SefariaError strings (budget caps, response-shape drift) never reach the
// reader as raw jargon. The KEY case: the session request budget never resets
// in-session, so a Retry on a budget error is structurally guaranteed to fail
// forever - it must offer a page Refresh (which resets module state) instead.
// Native (non-Sefaria) errors keep their own message: at the real call sites
// those are network/abort failures whose text is already user-legible.

import { SefariaError } from "./sefaria.ts";

export interface FriendlyError {
  message: string;
  // "refresh" -> only a page reload can recover (an in-session cap is spent);
  // "retry"   -> a re-fetch can plausibly succeed.
  action: "retry" | "refresh";
}

export function humanizeError(err: unknown): FriendlyError {
  if (err instanceof SefariaError) {
    // status 0 + "budget" == the hard session cap; a re-fetch can never succeed.
    if (err.status === 0 && /budget/i.test(err.message)) {
      return {
        message: "You've explored a lot this session. Refresh the page to keep going.",
        action: "refresh",
      };
    }
    if (err.status >= 500) {
      return { message: "Sefaria is having a moment - try again in a few seconds.", action: "retry" };
    }
    // status 0 == shape drift / parse / "no text"; internal detail, not for the learner.
    if (err.status === 0) {
      return { message: "That source didn't come through cleanly. Try again.", action: "retry" };
    }
    // other 4xx
    return { message: "Couldn't load that source. Try again.", action: "retry" };
  }
  // Native network / abort errors: their own message is already user-legible.
  if (err instanceof Error && err.message) {
    return { message: err.message, action: "retry" };
  }
  return { message: "Couldn't load - please try again.", action: "retry" };
}
